<script setup>
import { computed, onMounted } from "vue";
import { useStore } from "vuex";
import { NumberFormat } from "vue-i18n";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { trackPDPPageVisit } from "components/pod/pod-build-order/utils/ga_tracker";
import { redirectTo } from "common/utils/browser";
import {
  calculateProductTotalPrice,
  compositionPrice
} from "components/pod/pod-build-order/utils/pricing";
import {
  trackPrintReviewsClick,
  trackExploreMoreApparelClick
} from "components/pod/pod-build-order/utils/tracker";
import { writeCookie, readCookie } from "common/utils/cookie";
import { productEngagementTracker } from "components/jiffy/product-details/product-engagement-tracker";
import SoftnessScore from "./SoftnessScore.vue";
import ProductQuality from "./ProductQuality.vue";
import ProductColors from "./ProductColors.vue";
import ProductSizes from "./ProductSizes.vue";
import ShippingFreeBar from "./ShippingFreeBar.vue";

const store = useStore();
const product = computed(() => store.getters["product/selectedProduct"]);
const composition = computed(() => store.getters["product/selectedComposition"]);
const defaultComposition = computed(() => product.value.compositions.find(comp => comp.default));
const freeShippingThreshold = computed(() => store.getters["checkout/freeShippingThreshold"]);
const totalsNoDiscount = computed(() => store.getters["product/totalsNoDiscount"]);
const deliveryDate = computed(() => store.getters["delivery/deliveryDate"]);
const isDeliveryLoading = computed(() => store.getters["loading/delivery"]);
const prices = computed(() => store.getters["product/prices"]);
const variantQuantities = computed(() => store.getters["product/variantQuantities"]);
const assumptiveQuantity = computed(() => store.getters["product/assumptiveQuantity"]);
const isFreeShipping = computed(() => totalsNoDiscount.value - freeShippingThreshold.value > 0);
const isArtworkUploading = computed(() => store.getters["loading/uploadArtwork"]);
const isUploadInProgress = computed(() => store.getters["loading/isUploadInProgress"]);
const artwork = computed(() => store.getters["artwork/artwork"]);
const isUploadable = computed(() => {
  if (isArtworkUploading.value) return false;
  return !artwork.value;
});

const isSignedIn = computed(() => Boolean(readCookie("name")));

onMounted(() => {
  if (composition.value) {
    trackPDPPageVisit(product, composition.value, currentCompositionPrice.value);
    store.dispatch("product/fetchInventory", product.value);
  }

  // Delegate engagement tracking to the shared tracker singleton
  if (isSignedIn.value && product.value?.id && composition.value) {
    productEngagementTracker.trackCompositionChange({
      compositionId: composition.value.id,
      compositionName: composition.value.name,
      productId: product.value.id
    });
  }
});

const calculateCompositionPrice = compositionToCalculate =>
  compositionPrice({
    composition: compositionToCalculate,
    prices: prices.value,
    productTotalPrice: productTotalPrice.value,
    totalsNoDiscount: totalsNoDiscount.value,
    assumptiveQuantity: assumptiveQuantity.value
  });

const productTotalPrice = computed(() =>
  calculateProductTotalPrice({
    product: product.value,
    prices: prices.value,
    variantQuantities: variantQuantities.value,
    totalsNoDiscount: totalsNoDiscount.value
  })
);
const currentCompositionPrice = computed(() => {
  const price = calculateCompositionPrice(composition.value);
  if (price) return price;
  return calculateCompositionPrice(defaultComposition.value) || 0;
});

const handleReviewClick = () => {
  trackPrintReviewsClick(product.value);
};

const handleUpload = () => {
  document.getElementById("field-artwork-upload")?.click();
};

const handleExplore = () => {
  trackExploreMoreApparelClick();
  writeCookie(
    "explore_product_composition",
    JSON.stringify({ compositionId: composition.value.id, productId: product.value.id }),
    300
  );
  redirectTo("/made");
};
</script>

<template>
  <div class="product-details">
    <button v-if="isUploadable" class="product-details__upload" @click="handleUpload">
      <UseSvg class="product-details__upload-icon" icon="upload-cloud" type="print" />
      Start Image Upload
    </button>

    <div class="product-details__preview">
      <img class="product-details__image" :src="composition.imageUrl" :alt="product.name" />

      <ProductQuality
        v-if="product.quality"
        :quality="product.quality"
        class="product-details__quality"
      />

      <div class="product-details__pricing">
        <NumberFormat
          v-if="currentCompositionPrice"
          class="product-details__price"
          tag="div"
          :value="currentCompositionPrice"
          format="currency"
          scope="global"
        >
          <template #currency="slotProps">
            <span>{{ slotProps.currency }}</span>
          </template>
          <template #integer="slotProps">
            <span class="product-details__price-integer">{{ slotProps.integer }}</span>
          </template>
        </NumberFormat>

        <div v-if="product.cozyfeelScore" class="product-details__softness-score">
          <SoftnessScore :value="product.cozyfeelScore" />
        </div>
      </div>
    </div>

    <div class="product-details__content">
      <div class="product-details__info">
        <div class="product-details__brand">{{ product.brandAndCatalogNumber }}</div>
        <div class="product-details__name">{{ product.name }}</div>
      </div>

      <div class="product-details__rating" @click="handleReviewClick">
        <UseSvg icon="star-fill" class="product-details__rating-icon" />
        <span class="product-details__rating-average">{{ product.averageRating }}</span> ({{
          product.reviewsCount
        }})
      </div>

      <div class="product-details__delivery">
        <div v-if="isFreeShipping">Free Delivery</div>
        <div v-if="deliveryDate.full && !isDeliveryLoading" class="product-details__delivery-date">
          {{ deliveryDate.full }}
        </div>
        <div
          v-else-if="isDeliveryLoading"
          class="product-details__delivery-date product-details__delivery-date--loading"
        ></div>
      </div>

      <button
        v-if="isUploadable"
        class="product-details__upload product-details__upload--desktop"
        @click="handleUpload"
      >
        <UseSvg class="product-details__upload-icon" icon="upload-cloud" type="print" />
        Start Image Upload
      </button>

      <div class="js-selected-composition-scroll-target product-details__variants">
        <ProductColors :selected-composition="composition" :compositions="product.compositions" />
        <ProductSizes
          :variants="composition.variants"
          :product="product"
          :composition="composition"
          class="product-details__size"
        />
        <ShippingFreeBar class="product-details__shipping" />
      </div>

      <button
        class="product-details__explore"
        :disabled="isUploadInProgress"
        @click="handleExplore"
      >
        Explore More Apparel
      </button>
    </div>
  </div>
</template>

<style lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.product-details {
  max-width: $container-width;

  @include with-desktop {
    display: flex;
    gap: 32px;
  }

  &__content {
    @include with-desktop {
      flex-shrink: 0;
      width: 567px;
    }
  }

  &__upload {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 7px 16px;
    width: 100%;
    appearance: none;
    border: none;
    background-color: $pod-upload-bg;
    color: white;
    border-radius: 12px;
    text-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);
    font-size: 16px;
    font-weight: 600;
    line-height: 1.43;

    &:hover {
      background-color: $pod-guardsman-vivid;
    }

    @include with-desktop {
      display: none;
    }

    &--desktop {
      display: none;

      @include with-desktop {
        display: flex;
        margin: 16px 0 32px;
      }
    }
  }

  &__upload-icon {
    width: 35px;
    height: 35px;
  }

  &__info {
    padding: 8px 0;
  }

  &__brand {
    color: $pod-gray-modern-500;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;

    @include with-desktop {
      font-size: 14px;
    }
  }

  &__name {
    color: $pod-gray-modern-900;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;

    @include with-desktop {
      font-size: 18px;
    }
  }

  &__rating {
    border-top: solid 1px $pod-gray-modern-200;
    padding: 12px 0 8px;
    display: flex;
    align-items: center;
    gap: 2px;
    font-size: 12px;
    font-weight: 400;
    width: 100%;
    color: $pod-gray-modern-500;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.14;
  }

  &__rating-average {
    color: $pod-gray-modern-900;
    font-weight: 700;
  }

  &__rating-icon {
    color: $accent-grapefruit;
    width: 13px;
    height: 13px;
  }

  &__preview {
    text-align: center;
    margin: 8px 0;
    position: relative;
    padding: 10px 0;
    text-align: center;
    border-radius: 4px;
    background: $pod-gray-modern-100;

    @include with-desktop {
      flex: 1;
      flex-shrink: 0;
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &__pricing {
    position: absolute;
    bottom: 12px;
    left: 12px;
    display: flex;
    gap: 8px;
  }

  &__image {
    mix-blend-mode: multiply;
    max-width: 249px;
    max-height: 311px;
    object-fit: cover;
    display: inline-block;

    @include with-desktop {
      max-width: 100%;
      max-height: 637px;
    }
  }

  &__softness-score {
    width: 63px;
    height: 30px;
    background: $spindle-light;
    border-radius: 4px;
  }

  &__quality {
    position: absolute;
    top: 10px;
    left: 9px;
  }

  &__explore {
    cursor: pointer;
    align-items: center;
    appearance: none;
    border: none;
    border-radius: 6px;
    background: $pod-gray-modern-800;
    color: white;
    font-size: 14px;
    font-weight: 600;
    padding: 14px 16px;
    display: block;
    width: 100%;
    margin: 32px 0;

    &:hover {
      background: $pod-gray-modern-900;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    @include with-desktop {
      max-width: 300px;
      margin: 32px auto;
    }
  }

  &__delivery {
    padding: 8px 0;
    color: $pod-gray-modern-900;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.14;
    min-height: 42px;
  }

  &__delivery-date {
    color: $pod-date-orange;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.2;
    min-height: 22px;

    &--loading {
      @include vlc-animation;

      position: relative;
      display: inline-block;
      min-width: 200px;
      border-radius: 6px;
      background-color: $pod-gray-modern-100;
    }
  }

  &__price {
    min-width: 58px;
    height: 30px;
    background: $red-light;
    color: white;
    border-radius: 4px;
    display: flex;
    padding: 7px 12px 5px 13px;
    justify-content: center;
    align-items: flex-start;
    gap: 1.5px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
  }

  &__price-integer {
    font-size: 17px;
    line-height: 15px;
  }

  &__variants {
    padding: 16px 8px 24px;
    margin: 24px 0 32px;
    background: $pod-gray-modern-100;
    border-radius: 4px;

    @include with-desktop {
      background: none;
      padding: 0;
      border-radius: 0;
      margin: 24px 0 0;
    }
  }

  & &__size {
    margin-top: 24px;
  }

  &__shipping {
    margin-top: 32px;

    @include with-desktop {
      margin-top: 48px;
    }
  }
}
</style>
